import React, { useState, useEffect, useRef, useMemo } from 'react';
import PrivacyPolicy from './PrivacyPolicy';
import UpgradeModal from './UpgradeModal';
import { Send, Brain, Target, TrendingUp, AlertCircle, CheckCircle, Loader,
         FileText, Presentation, BarChart3, Users, Shield,
         SkipForward, Zap, Mail, X, Download, ArrowLeft } from 'lucide-react';
import {
  Document, Packer, Paragraph, TextRun,
  Table, TableRow, TableCell,
  AlignmentType, BorderStyle, WidthType, ShadingType,
  Header, Footer, PageNumber,
  VerticalAlign, TableLayoutType, PageBreak,
} from 'docx';
import PptxGenJS from 'pptxgenjs';
import { saveAs } from 'file-saver';
import { useUser, useAuth, UserButton } from '@clerk/clerk-react';
import { createSupabaseClient } from '../lib/supabase';

const TOTAL_QUESTIONS = 7;

// Disinformation Detection Module
const DisinformationDetector = {
  async analyzeContent(content) {
    const indicators = {
      emotionalLanguage: /\b(shocking|unbelievable|you won't believe|breaking|urgent|exclusive)\b/gi,
      allCaps: /\b[A-Z]{4,}\b/g,
      excessivePunctuation: /[!?]{2,}/g,
      clickbait: /\b(one weird trick|doctors hate|click here|limited time)\b/gi,
      vagueSources: /\b(experts say|studies show|sources claim|reportedly)\b/gi,
      absoluteLanguage: /\b(always|never|everyone|nobody|all|none|absolutely|completely)\b/gi
    };

    let score = 0;
    const flags = [];

    // Check for indicators
    if (indicators.emotionalLanguage.test(content)) {
      score += 20;
      flags.push('Emotional/sensational language detected');
    }
    if ((content.match(indicators.allCaps) || []).length > 3) {
      score += 15;
      flags.push('Excessive capitalization');
    }
    if (indicators.excessivePunctuation.test(content)) {
      score += 10;
      flags.push('Excessive punctuation');
    }
    if (indicators.clickbait.test(content)) {
      score += 25;
      flags.push('Clickbait patterns detected');
    }
    if ((content.match(indicators.vagueSources) || []).length > 2) {
      score += 15;
      flags.push('Vague source attribution');
    }
    if ((content.match(indicators.absoluteLanguage) || []).length > 5) {
      score += 15;
      flags.push('Excessive absolute language');
    }

    return {
      score: Math.min(score, 100),
      risk: score > 60 ? 'HIGH' : score > 30 ? 'MEDIUM' : 'LOW',
      flags,
      filtered: score > 60
    }
  }
};

// StorageModule replaced by Supabase — see analyses table

const AnalyticalFrameworkTool = () => {
  // Clerk auth hooks (called at top level per React rules)
  const { user, isLoaded: isUserLoaded } = useUser();
  const { getToken } = useAuth();

  // Supabase client bound to the current Clerk session
  const supabase = useMemo(() => createSupabaseClient(getToken), [getToken]);

  const [stage, setStage] = useState('intro');
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState({
    initialProblem: '',
    responses: [],
    currentQuestion: 0
  });
  const [recommendation, setRecommendation] = useState(null);
  const [executionOutput, setExecutionOutput] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState('balanced'); // balanced, mainstream, contrarian
  const [indicators, setIndicators] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [subscriptionDetail, setSubscriptionDetail] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef(null);

  const isInitialMount = useRef(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Don't scroll on initial mount (welcome message)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    scrollToBottom();
  }, [messages]);

  // Fetch usage info from Supabase
  useEffect(() => {
    if (isUserLoaded && user) {
      fetchUsageInfo();
      fetchSubscriptionDetail();
    }
  }, [isUserLoaded, user]);

  const fetchUsageInfo = async () => {
    if (!supabase) return; // Supabase not configured
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('analyses_used_this_month, monthly_limit, subscription_tier')
        .eq('clerk_user_id', user.id)
        .maybeSingle();
      if (!error && data) {
        setUsageInfo(data);
      }
    } catch (err) {
      // Profile may not exist yet (first visit, sync in progress)
    }
  };

  const fetchSubscriptionDetail = async () => {
    try {
      const token = await getToken();
      const res = await fetch('/api/subscription-info', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionDetail(data);
      }
    } catch (err) {
      // fail silently
    }
  };

  const checkUsageLimit = async () => {
    await fetchUsageInfo();
    if (usageInfo && usageInfo.analyses_used_this_month >= usageInfo.monthly_limit) {
      setShowUpgradeModal(true);
      return false;
    }
    return true;
  };

  // Initialize with welcome message
  useEffect(() => {
    setMessages([{
      type: 'system',
      content: `Welcome to Loyal Sidekick

Need a loyal guide to help you turn a vague or muddy question into a clear, structured query? Loyal Sidekick takes your political, climate or economic forecasting question and gives you the best structured analytical framework to tackle it, and lays out exactly how to collect data and perform the analysis.

You may ask:
• What's the likelihood of a major drought in any region in the next 12 months which could cascade into armed conflict?
• What are the most plausible political scenarios for Venezuela for the next two years and which political figures should I focus on?
• Which geopolitical variables would have the greatest impact on the growth of data centres in the Middle East?

You can toggle between a Balanced analytical mode, a Mainstream "conventional wisdom" approach, or a "Contrarian" sidekick who pushes back a bit more.

Enhanced with:
→ Disinformation detection across all content
→ Persistent analysis tracking
→ Dual-persona analysis (mainstream + contrarian)
→ Export to DOCX/PPTX
→ Automated indicator monitoring

What analytical problem would you like to tackle today?`,
      timestamp: new Date()
    }]);
  }, []);

  // Auto-start Stripe Checkout when arriving from homepage pricing table (?plan=...)
  useEffect(() => {
    if (!isUserLoaded || !user) return;
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (!plan) return;
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch('/api/create-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ priceKey: plan })
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else setShowUpgradeModal(true);
      } catch {
        setShowUpgradeModal(true);
      }
    })();
  }, [isUserLoaded, user]);

  // Show success message when returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') === '1') {
      setMessages(prev => [...prev, {
        type: 'system',
        content: 'Subscription activated! Your new plan limits are now in effect. Thank you for upgrading.',
        timestamp: new Date()
      }]);
      // Remove the query param from the URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
      fetchUsageInfo();
      fetchSubscriptionDetail();
    }
  }, []);

  const generateDiagnosticQuestion = async (problemStatement, previousResponses, persona) => {
    const questionNumber = previousResponses.length + 1;
    
    // Persona-specific prompting
    const personaContext = persona === 'contrarian' 
      ? '\n\nADOPT A CONTRARIAN ANALYTICAL STANCE: Challenge conventional wisdom, look for overlooked variables, question mainstream assumptions, and probe for alternative explanations that others might miss.'
      : persona === 'mainstream'
      ? '\n\nADOPT A MAINSTREAM ANALYTICAL STANCE: Follow established best practices, rely on conventional wisdom, emphasize consensus views, and prioritize proven analytical frameworks.'
      : '';

    const systemPrompt = `You are an expert analytical coach specializing in structured analytical techniques, geopolitical risk analysis, and forecasting methodologies.${personaContext}

Your role is to conduct a diagnostic conversation to understand:
1. The analyst's thought process and analytical approach
2. Their domain expertise and familiarity with the subject
3. The complexity and time horizon of the problem
4. Data availability and constraints
5. Stakeholder needs and decision context
6. Their cognitive biases and analytical blind spots
7. Critical indicators and monitoring requirements

You are on question ${questionNumber} of ${TOTAL_QUESTIONS}. Each question should build on previous answers to go deeper.

ANALYTICAL TECHNIQUES AVAILABLE:
- Alan's 7-Step Methodology (Problem Definition → Decomposition → Source Collection → Technique Selection → Indicator Monitoring → Scenario Development → Output Generation)
- CIA Structured Analytical Techniques (ACH, Key Assumptions Check, Devil's Advocacy, Red Team Analysis, Indicators, What-If Analysis, Scenario Analysis)
- Superforecasting (Base rates, outside view, Fermi estimation, granular probability assessment)
- Bayesian Reasoning (Prior probability, evidence updating, likelihood ratios)
- Backcasting (Work backward from desired future state)
- STEMPLES+ (Social, Technology, Economic, Military, Political, Legal, Environmental, Security + additional factors)

QUESTION STRATEGY BY NUMBER:
Q1: Problem scope (what outcome to assess, why it matters)
Q2: Time horizons and measurability criteria
Q3: Data sources and collection strategy
Q4: Analyst expertise and domain knowledge
Q5: Decision context and stakeholder needs
Q6: Cognitive biases and analytical blind spots
Q7: Critical indicators and monitoring frequency

Problem Statement: ${problemStatement}
Previous Responses: ${JSON.stringify(previousResponses, null, 2)}

Generate the next diagnostic question. Be conversational, Socratic, and insightful. The question should be specific, build on what you've learned, and help you recommend the optimal analytical framework.

Respond with ONLY the question text, no preamble.`;

    try {
      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 1000,
          messages: [
            { role: 'user', content: systemPrompt }
          ]
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const textBlock = data.content?.find(b => b.type === 'text');
      return textBlock?.text || '';
    } catch (error) {
      console.error('Error generating question:', error);
      return `Error: ${error.message}. Please try again — if the problem persists, contact support.`;
    }
  };

  const generateRecommendation = async (problemStatement, responses, persona) => {
    const personaContext = persona === 'contrarian'
      ? '\n\nANALYTICAL STANCE: Contrarian - challenge assumptions, seek alternative explanations, question consensus.'
      : persona === 'mainstream'
      ? '\n\nANALYTICAL STANCE: Mainstream - follow best practices, emphasize proven frameworks, build on consensus.'
      : '';

    const systemPrompt = `You are an expert analytical coach. Based on this diagnostic conversation, recommend the optimal analytical technique(s) and create a tailored workflow.${personaContext}

Problem Statement: ${problemStatement}

Diagnostic Responses:
${responses.map((r, i) => `Q${i+1}: ${r.question}\nA${i+1}: ${r.answer}`).join('\n\n')}

AVAILABLE TECHNIQUES:
1. Alan's 7-Step Methodology
2. Analysis of Competing Hypotheses (ACH)
3. Key Assumptions Check
4. Devil's Advocacy
5. Red Team Analysis
6. Indicators/Signposts of Change
7. What-If Analysis
8. Scenario Analysis
9. Superforecasting Framework
10. Bayesian Reasoning
11. Backcasting
12. STEMPLES+ Analysis

Provide a recommendation in this JSON format:
{
  "primaryTechnique": "name of primary technique",
  "secondaryTechnique": "name of secondary technique (optional)",
  "rationale": "2-3 sentence explanation of why these techniques are optimal for this analyst and problem",
  "workflow": [
    "Step 1: clear action",
    "Step 2: clear action",
    "Step 3: clear action",
    "Step 4: clear action",
    "Step 5: clear action"
  ],
  "strengths": ["strength 1 based on diagnostic", "strength 2"],
  "watchouts": ["potential bias or gap to watch for", "another consideration"],
  "estimatedTime": "time estimate for this analysis",
  "criticalIndicators": ["Indicator 1 to monitor", "Indicator 2 to monitor", "Indicator 3 to monitor"]
}

Respond with ONLY valid JSON, no markdown formatting.`;

    try {
      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 4000,
          messages: [{ role: 'user', content: systemPrompt }],
          stream: true
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `API request failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const eventData = line.slice(6).trim();
          if (!eventData) continue;
          try {
            const event = JSON.parse(eventData);
            if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') {
              fullText += event.delta.text;
            }
          } catch {}
        }
      }
      const jsonMatch = fullText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('No JSON found in response');
      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.error('Error generating recommendation:', error);
      return null;
    }
  };

  const executeFramework = async () => {
    if (!recommendation) return;

    const personaContext = selectedPersona === 'contrarian'
      ? '\n\nADOPT A CONTRARIAN ANALYTICAL STANCE: Challenge mainstream assumptions, explore alternative scenarios that conventional analysis overlooks, and identify weak signals that others dismiss.'
      : selectedPersona === 'mainstream'
      ? '\n\nADOPT A MAINSTREAM ANALYTICAL STANCE: Follow established analytical conventions, emphasize consensus forecasts, and build on proven frameworks.'
      : '';

    const systemPrompt = `You are executing an analytical framework for the user.${personaContext}

Problem: ${diagnosticData.initialProblem}

Technique: ${recommendation.primaryTechnique}${recommendation.secondaryTechnique ? ` + ${recommendation.secondaryTechnique}` : ''}

Workflow:
${recommendation.workflow.map((step, i) => `${i+1}. ${step}`).join('\n')}

Analyst's Context (from diagnostic):
${diagnosticData.responses.map((r, i) => `Q: ${r.question}\nA: ${r.answer}`).join('\n\n')}

Execute this analytical framework step-by-step. Be thorough, structured, and provide actionable outputs at each step. Format your response as a clear, professional analytical product.

CRITICAL: At the end, provide a MONITORING SECTION with specific indicators formatted as JSON:
{
  "indicators": [
    {
      "name": "Indicator name",
      "description": "What to monitor",
      "source": "Where to monitor this",
      "threshold": "What change signals importance",
      "frequency": "How often to check"
    }
  ]
}

Include:
- Clear problem statement (testable and specific)
- Key analytical units/factors to examine
- Specific sources or data to collect
- Probability assessments where relevant
- Indicators to monitor (with the JSON structure above)
- Alternative scenarios
- Next steps

Use your expertise in geopolitical risk analysis, intelligence tradecraft, and structured analytical techniques.`;

    try {
      setIsProcessing(true);
      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 4000,
          messages: [
            { role: 'user', content: systemPrompt }
          ],
          stream: true
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `API request failed with status ${response.status}`);
      }

      // Read SSE stream and accumulate text — avoids Vercel 504 on long generations
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let output = '';
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const eventData = line.slice(6).trim();
          if (!eventData) continue;
          try {
            const event = JSON.parse(eventData);
            if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') {
              output += event.delta.text;
            }
          } catch {}
        }
      }
      if (!output) throw new Error('No output received from AI service');

      
      // Extract indicators from output
      let parsedIndicators = [];
      const indicatorMatch = output.match(/\{[\s\S]*"indicators"[\s\S]*\}/);
      if (indicatorMatch) {
        try {
          const indicatorData = JSON.parse(indicatorMatch[0]);
          parsedIndicators = indicatorData.indicators || [];
          setIndicators(parsedIndicators);
        } catch (e) {
          console.error('Failed to parse indicators:', e);
        }
      }

      // Strip the ## Monitoring Section and its JSON block — rendered separately in the Indicator Dashboard
      const cleanOutput = output
        .replace(/#+\s*Monitoring Section[\s\S]*$/, '')
        .replace(/```json[\s\S]*?```/gm, '')
        .trim();
      setExecutionOutput(cleanOutput);
      setStage('execution');

      // Save to Supabase
      if (user && supabase) {
        try {
          const { error: insertError } = await supabase
            .from('analyses')
            .insert({
              clerk_user_id: user.id,
              problem_statement: diagnosticData.initialProblem,
              persona: selectedPersona,
              recommendation,
              output,
              indicators: parsedIndicators
            });
          if (insertError) console.error('Save error:', insertError);

          // Increment usage counter via server-side endpoint
          const token = await getToken();
          await fetch('/api/increment-usage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          // Refresh usage info
          await fetchUsageInfo();
        } catch (saveErr) {
          console.error('Error saving analysis:', saveErr);
        }
      }

      setMessages(prev => [...prev, {
        type: 'system',
        content: 'Framework execution complete. Analysis saved to your history.',
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error executing framework:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        content: `Error executing framework: ${error.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const skipToDiagnostic = async () => {
    if (!currentInput.trim()) return;

    setIsProcessing(true);
    const userMessage = {
      type: 'user',
      content: currentInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setDiagnosticData(prev => ({
      ...prev,
      initialProblem: currentInput
    }));

    // Generate quick recommendation without full diagnostic
    const quickSystemPrompt = `Based ONLY on this problem statement, recommend an analytical technique:

Problem: ${currentInput}

Provide recommendation in JSON format:
{
  "primaryTechnique": "technique name",
  "rationale": "brief explanation",
  "workflow": ["step 1", "step 2", "step 3", "step 4", "step 5"],
  "strengths": ["strength 1", "strength 2"],
  "watchouts": ["watchout 1", "watchout 2"],
  "estimatedTime": "time estimate",
  "criticalIndicators": ["indicator 1", "indicator 2", "indicator 3"]
}`;

    try {
      const response = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 4000,
          messages: [{ role: 'user', content: quickSystemPrompt }],
          stream: true
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `API request failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const eventData = line.slice(6).trim();
          if (!eventData) continue;
          try {
            const event = JSON.parse(eventData);
            if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') {
              fullText += event.delta.text;
            }
          } catch {}
        }
      }
      const jsonMatch = fullText.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('No JSON found in response');
      const rec = JSON.parse(jsonMatch[0]);

      setRecommendation(rec);
      setStage('recommendation');
      setCurrentInput('');
    } catch (error) {
      console.error('Error generating quick recommendation:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        content: `Error: ${error.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSend = async () => {
    if (!currentInput.trim() || isProcessing) return;

    // Check usage limits before starting a new analysis
    if (stage === 'intro') {
      const canProceed = await checkUsageLimit();
      if (!canProceed) return;
    }

    // Check for disinformation
    const disinfoCheck = await DisinformationDetector.analyzeContent(currentInput);
    
    if (disinfoCheck.filtered) {
      setMessages(prev => [...prev, {
        type: 'warning',
        content: `⚠️ DISINFORMATION ALERT: Content flagged as high-risk (${disinfoCheck.score}/100)\n\nFlags: ${disinfoCheck.flags.join(', ')}\n\nThis content has been filtered from the analysis chain. Please provide credible sources.`,
        timestamp: new Date()
      }]);
      setCurrentInput('');
      return;
    } else if (disinfoCheck.risk === 'MEDIUM') {
      setMessages(prev => [...prev, {
        type: 'warning',
        content: `⚠️ Content quality alert (${disinfoCheck.score}/100): ${disinfoCheck.flags.join(', ')}. Proceeding with caution.`,
        timestamp: new Date()
      }]);
    }

    const userMessage = {
      type: 'user',
      content: currentInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setIsProcessing(true);

    if (stage === 'intro') {
      setDiagnosticData(prev => ({
        ...prev,
        initialProblem: currentInput
      }));
      
      setMessages(prev => [...prev, {
        type: 'system',
        content: `Starting ${TOTAL_QUESTIONS}-question diagnostic with ${selectedPersona} analytical stance.`,
        timestamp: new Date()
      }]);

      setStage('diagnostic');
      
      const firstQuestion = await generateDiagnosticQuestion(currentInput, [], selectedPersona);
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: firstQuestion,
        timestamp: new Date()
      }]);
      
      setIsProcessing(false);
      
    } else if (stage === 'diagnostic') {
      const newResponse = {
        question: messages[messages.length - 1].content,
        answer: currentInput
      };
      
      const updatedResponses = [...diagnosticData.responses, newResponse];
      setDiagnosticData(prev => ({
        ...prev,
        responses: updatedResponses
      }));

      if (updatedResponses.length < TOTAL_QUESTIONS) {
        const nextQuestion = await generateDiagnosticQuestion(
          diagnosticData.initialProblem,
          updatedResponses,
          selectedPersona
        );
        
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: nextQuestion,
          timestamp: new Date()
        }]);
        
        setIsProcessing(false);
      } else {
        setMessages(prev => [...prev, {
          type: 'system',
          content: 'Diagnostic complete. Generating technique recommendation...',
          timestamp: new Date()
        }]);

        const rec = await generateRecommendation(
          diagnosticData.initialProblem,
          updatedResponses,
          selectedPersona
        );

        if (rec) {
          setRecommendation(rec);
          setStage('recommendation');
        } else {
          setMessages(prev => [...prev, {
            type: 'error',
            content: 'Analysis took too long or encountered an error. Please try again.',
            timestamp: new Date()
          }]);
        }
        setIsProcessing(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const exportToDOCX = async () => {
    if (!executionOutput) return;

    try {
      // ── Colour palette (matches ls-briefing-template.docx) ──
      const C = {
        ink: '1a1a1a', ink2: '4a4a4a', muted: '888888',
        accent: '2c4a6e', gold: 'c9a84c', bg: 'f7f6f3',
        white: 'ffffff', div: 'e2e0db',
        red: 'b83232', amber: 'c97a2c', green: '2c8a50',
      };

      // ── Document metadata ──
      const reportTitle = diagnosticData.initialProblem || 'Analytical Framework Output';
      const dateIssued = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const reportId = `LS-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      const analyticalMode = selectedPersona.charAt(0).toUpperCase() + selectedPersona.slice(1);
      const nextReview = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const execSummary = recommendation?.rationale || executionOutput.slice(0, 600);

      // ── Border helpers ──
      const noBorders = {
        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
      };
      const cellBorders = {
        top:    { style: BorderStyle.SINGLE, size: 4, color: C.div },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: C.div },
        left:   { style: BorderStyle.SINGLE, size: 4, color: C.div },
        right:  { style: BorderStyle.SINGLE, size: 4, color: C.div },
      };

      // ── Paragraph helpers ──
      const spacer = (pt = 6) => new Paragraph({ spacing: { before: 0, after: pt * 20 } });

      const h2 = txt => new Paragraph({
        children: [new TextRun({ text: txt, bold: true, size: 28, color: C.accent })],
        spacing: { before: 400, after: 120 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: C.div, space: 4 } },
      });

      const h3 = txt => new Paragraph({
        children: [new TextRun({ text: txt, bold: true, size: 22, color: C.accent })],
        spacing: { before: 280, after: 80 },
      });

      const bodyP = (txt, italic = false) => new Paragraph({
        children: [new TextRun({ text: txt, size: 20, color: C.ink2, italics: italic })],
        spacing: { before: 0, after: 120 },
      });

      const noteP = txt => new Paragraph({
        children: [new TextRun({ text: txt, size: 17, color: C.muted, italics: true })],
        spacing: { before: 60, after: 120 },
        indent: { left: 480 },
      });

      // ── Table cell helpers ──
      const thCell = (txt, w) => new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: txt, bold: true, size: 16, color: C.white, allCaps: true, characterSpacing: 60 })],
        })],
        shading: { type: ShadingType.SOLID, color: C.accent },
        width: { size: w, type: WidthType.PERCENTAGE },
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
        borders: noBorders,
      });

      const tdCell = (txt, color = C.ink2, shade = C.white, bold = false) => new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: txt, size: 19, color, bold })] })],
        shading: { type: ShadingType.SOLID, color: shade },
        borders: cellBorders,
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
      });

      const pageBreak = () => new Paragraph({ children: [new PageBreak()] });

      // ── COVER ──
      const cover = [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: [new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: 'ANALYTICAL OUTPUT', bold: true, size: 18, color: C.white, allCaps: true, characterSpacing: 200 })],
              alignment: AlignmentType.CENTER,
            })],
            shading: { type: ShadingType.SOLID, color: C.accent },
            margins: { top: 100, bottom: 100, left: 200, right: 200 },
            borders: noBorders,
          })] })],
          layout: TableLayoutType.FIXED,
        }),
        spacer(8),
        new Paragraph({
          children: [new TextRun({ text: 'ANALYTICAL FRAMEWORK BRIEFING', size: 18, color: C.gold, bold: true, allCaps: true, characterSpacing: 120 })],
          spacing: { before: 320, after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: reportTitle, bold: true, size: 52, color: C.ink })],
          spacing: { before: 0, after: 160 },
        }),
        new Paragraph({
          children: [new TextRun({ text: dateIssued, size: 22, color: C.muted, italics: true })],
          spacing: { before: 0, after: 360 },
        }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: [
              thCell('Date Issued', 17), thCell('Report ID', 17), thCell('Classification', 17),
              thCell('Mode', 17), thCell('Distribution', 17), thCell('Next Review', 15),
            ]}),
            new TableRow({ children: [
              tdCell(dateIssued,          C.ink,    C.white, true),
              tdCell(reportId,            C.accent, C.bg),
              tdCell('ANALYTICAL OUTPUT', C.ink,    C.white),
              tdCell(analyticalMode,      C.accent, C.bg),
              tdCell('Authorized Users',  C.ink,    C.white),
              tdCell(nextReview,          C.accent, C.bg),
            ]}),
          ],
          layout: TableLayoutType.FIXED,
        }),
      ];

      // ── S1: EXECUTIVE SUMMARY ──
      const s1 = [
        h2('1. EXECUTIVE SUMMARY'),
        bodyP(execSummary),
        spacer(4),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: [thCell('Analytical Level', 34), thCell('Primary Technique', 33), thCell('Analytical Mode', 33)] }),
            new TableRow({ children: [
              tdCell('Assessed',                                           C.accent, C.bg,    true),
              tdCell(recommendation?.primaryTechnique || 'Not specified',  C.ink,    C.white),
              tdCell(analyticalMode,                                        C.accent, C.bg),
            ]}),
          ],
          layout: TableLayoutType.FIXED,
        }),
      ];

      // ── S2: ANALYTICAL WORKFLOW (from recommendation.workflow) ──
      const workflowSteps = recommendation?.workflow || [];
      const kjRows = workflowSteps.slice(0, 7).map((step, i) =>
        new TableRow({ children: [
          tdCell(String(i + 1).padStart(2, '0'), C.muted,   i % 2 === 0 ? C.bg : C.white, true),
          tdCell(step,                            C.ink,     i % 2 === 0 ? C.bg : C.white),
          tdCell('Assessed',                      C.accent,  i % 2 === 0 ? C.bg : C.white),
          tdCell('Near-term',                     C.muted,   i % 2 === 0 ? C.bg : C.white),
        ]})
      );

      const s2 = [
        h2('2. ANALYTICAL WORKFLOW & KEY STEPS'),
        noteP('Core steps for executing the recommended analytical framework.'),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: [thCell('#', 5), thCell('Step', 55), thCell('Status', 20), thCell('Timeframe', 20)] }),
            ...(kjRows.length > 0 ? kjRows : [new TableRow({ children: [
              tdCell('—', C.muted, C.white), tdCell('No workflow steps defined.', C.ink2, C.white),
              tdCell('—', C.muted, C.white), tdCell('—', C.muted, C.white),
            ]})]),
          ],
          layout: TableLayoutType.FIXED,
        }),
      ];

      // ── S3: FULL ANALYSIS OUTPUT ──
      const analysisParas = executionOutput.split('\n\n').filter(s => s.trim()).map(para => {
        const isHeading = para.startsWith('#') ||
          (para.length < 120 && para === para.toUpperCase() && /[A-Z]/.test(para));
        return isHeading ? h3(para.replace(/^#+\s*/, '')) : bodyP(para);
      });

      const s3 = [h2('3. ANALYSIS OUTPUT'), ...analysisParas];

      // ── S4: MONITORING INDICATORS ──
      const indRows = indicators.length > 0
        ? indicators.map((ind, i) => new TableRow({ children: [
            tdCell(ind.name        || '—', C.ink,  i % 2 === 0 ? C.bg : C.white, true),
            tdCell(ind.description || '—', C.ink2, i % 2 === 0 ? C.bg : C.white),
            tdCell(ind.source      || '—', C.muted, i % 2 === 0 ? C.bg : C.white),
            tdCell(ind.frequency   || '—', C.muted, i % 2 === 0 ? C.bg : C.white),
          ]}))
        : [new TableRow({ children: [
            tdCell('No indicators defined for this analysis.', C.muted, C.white),
            tdCell('', C.muted, C.white), tdCell('', C.muted, C.white), tdCell('', C.muted, C.white),
          ]})];

      const s4 = [
        h2('4. MONITORING INDICATORS'),
        noteP('Key signals to track. Monitor at defined frequency and escalate when thresholds are crossed.'),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: [thCell('Indicator', 28), thCell('Description', 37), thCell('Source', 18), thCell('Frequency', 17)] }),
            ...indRows,
          ],
          layout: TableLayoutType.FIXED,
        }),
      ];

      // ── APPENDIX ──
      const appendix = [
        h2('APPENDIX'),
        h3('A. Methodology Note'),
        bodyP(`This briefing was produced using structured analytical techniques (SATs) via the Loyal Sidekick platform, TK Strategies Limited. Analytical mode: ${analyticalMode}. Primary technique: ${recommendation?.primaryTechnique || 'Structured Analysis'}. All assessments reflect analytical judgement, not statistical forecasts.`),
        h3('B. Next Review Date'),
        bodyP(nextReview, true),
        spacer(16),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: [new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Loyal Sidekick  ·  TK Strategies Limited', size: 16, color: C.white })],
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                children: [new TextRun({ text: 'Prepared for named distribution only. Do not forward without authorisation.', size: 15, color: 'c8d0da', italics: true })],
                alignment: AlignmentType.CENTER,
                spacing: { before: 60 },
              }),
            ],
            shading: { type: ShadingType.SOLID, color: C.accent },
            margins: { top: 160, bottom: 160, left: 200, right: 200 },
            borders: noBorders,
          })] })],
          layout: TableLayoutType.FIXED,
        }),
      ];

      // ── HEADER ──
      const docHeader = new Header({ children: [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: 'Loyal Sidekick  ·  TK Strategies Limited', size: 15, color: C.muted })] })],
              borders: { top: noBorders.top, left: noBorders.left, right: noBorders.right, bottom: { style: BorderStyle.SINGLE, size: 4, color: C.div } },
              margins: { bottom: 60 },
            }),
            new TableCell({
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: 'ANALYTICAL OUTPUT', size: 15, color: C.gold, bold: true, allCaps: true })] })],
              borders: { top: noBorders.top, left: noBorders.left, right: noBorders.right, bottom: { style: BorderStyle.SINGLE, size: 4, color: C.div } },
              margins: { bottom: 60 },
            }),
          ]})],
          layout: TableLayoutType.FIXED,
        }),
      ]});

      // ── FOOTER ──
      const docFooter = new Footer({ children: [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new TableRow({ children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: `${reportId}  ·  ${dateIssued}`, size: 15, color: C.muted })] })],
              borders: { top: { style: BorderStyle.SINGLE, size: 4, color: C.div }, bottom: noBorders.bottom, left: noBorders.left, right: noBorders.right },
              margins: { top: 60 },
            }),
            new TableCell({
              children: [new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: 'Page ', size: 15, color: C.muted }),
                  new TextRun({ children: [PageNumber.CURRENT], size: 15, color: C.muted }),
                ],
              })],
              borders: { top: { style: BorderStyle.SINGLE, size: 4, color: C.div }, bottom: noBorders.bottom, left: noBorders.left, right: noBorders.right },
              margins: { top: 60 },
            }),
          ]})],
          layout: TableLayoutType.FIXED,
        }),
      ]});

      // ── ASSEMBLE ──
      const doc = new Document({
        creator: 'Loyal Sidekick — TK Strategies Limited',
        title: reportTitle,
        description: 'Generated by Loyal Sidekick',
        styles: { paragraphStyles: [{ id: 'Normal', name: 'Normal',
          run: { font: { name: 'Segoe UI' }, color: C.ink2, size: 20 },
          paragraph: { spacing: { after: 120 } },
        }]},
        sections: [{
          properties: { page: { margin: { top: 900, bottom: 900, left: 1080, right: 1080 } } },
          headers: { default: docHeader },
          footers: { default: docFooter },
          children: [
            ...cover,   pageBreak(),
            ...s1,      pageBreak(),
            ...s2,      pageBreak(),
            ...s3,      pageBreak(),
            ...s4,      pageBreak(),
            ...appendix,
          ],
        }],
      });

      const blob = await Packer.toBlob(doc);
      const filename = `loyal-sidekick-${new Date().toISOString().split('T')[0]}-${reportId}.docx`;
      saveAs(blob, filename);

      setMessages(prev => [...prev, {
        type: 'system',
        content: `Word document exported: ${filename}`,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error exporting to DOCX:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        content: 'Failed to export Word document. Please try again.',
        timestamp: new Date()
      }]);
    }
  };

  const exportToPPTX = async () => {
    if (!executionOutput) return;

    try {
      const pptx = new PptxGenJS();
      pptx.layout  = 'LAYOUT_WIDE';
      pptx.author  = 'TK Strategies Limited';
      pptx.company = 'TK Strategies Limited';
      pptx.subject = 'Analytical Framework Briefing';
      pptx.title   = diagnosticData.initialProblem || 'Loyal Sidekick Analysis';

      // ── Colour palette (matches ls-briefing-template.pptx) ──
      const BG = 'F7F6F3', INK = '1A1A1A', INK2 = '4A4A4A', MUTED = '888888';
      const ACCENT = '2C4A6E', GOLD = 'C9A84C', WHITE = 'FFFFFF', DIV = 'E2E0DB';

      // ── Slide geometry (LAYOUT_WIDE = 13.33" × 7.5") ──
      const W = 13.33, H = 7.5;
      const HDR = 0.55, FTR = 0.32;
      const BY = HDR + 0.1;
      const PAD = 0.35;

      // ── Document metadata ──
      const reportTitle   = diagnosticData.initialProblem || 'Analytical Framework Output';
      const dateIssued    = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const reportId      = `LS-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      const analyticalMode = selectedPersona.charAt(0).toUpperCase() + selectedPersona.slice(1);
      const nextReview    = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

      // ── Reusable layout helpers ──
      const addHeader = (sl, section) => {
        sl.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: HDR, fill: { color: INK } });
        sl.addShape(pptx.ShapeType.rect, { x: 0.2, y: 0.1, w: 0.35, h: 0.35, fill: { color: GOLD }, line: { color: GOLD } });
        sl.addText('LS', { x: 0.2, y: 0.1, w: 0.35, h: 0.35, fontSize: 9, bold: true, color: INK, align: 'center', valign: 'middle' });
        sl.addText('Loyal Sidekick  ·  TK Strategies  ·  ' + section, { x: 0.65, y: 0.12, w: 8, h: 0.3, fontSize: 8, color: 'A0AABF' });
        sl.addText('ANALYTICAL OUTPUT', { x: W - 3.2, y: 0.09, w: 3, h: 0.18, fontSize: 7, bold: true, color: GOLD, align: 'right', charSpacing: 1.5 });
        sl.addText(reportId + '  ·  ' + dateIssued, { x: W - 3.2, y: 0.28, w: 3, h: 0.18, fontSize: 7, color: '555F70', align: 'right' });
      };

      const addFooter = (sl, num) => {
        sl.addShape(pptx.ShapeType.rect, { x: 0, y: H - FTR, w: W, h: FTR, fill: { color: WHITE }, line: { color: DIV } });
        sl.addText('Loyal Sidekick  ·  TK Strategies Limited', { x: PAD, y: H - FTR + 0.05, w: 7, h: 0.22, fontSize: 7, color: MUTED });
        sl.addText(`0${num} / 07`, { x: W - 1.2, y: H - FTR + 0.05, w: 0.9, h: 0.22, fontSize: 7, color: 'BBBBBB', align: 'right' });
      };

      const eyebrow = (sl, txt, x, y, w) =>
        sl.addText(txt, { x, y, w, h: 0.22, fontSize: 8, bold: true, color: ACCENT, charSpacing: 2 });

      const divider = (sl, x, y, w) =>
        sl.addShape(pptx.ShapeType.line, { x, y, w, h: 0, line: { color: DIV, width: 0.75 } });

      // ── SLIDE 1: COVER ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Analytical Framework Briefing');
        sl.addShape(pptx.ShapeType.rect, { x: 0, y: HDR, w: 0.06, h: H - HDR - FTR, fill: { color: ACCENT }, line: { color: ACCENT } });
        sl.addText('ANALYTICAL FRAMEWORK BRIEFING', { x: 0.45, y: 1.1, w: 9, h: 0.28, fontSize: 8, bold: true, color: GOLD, charSpacing: 2 });
        sl.addText(reportTitle, { x: 0.45, y: 1.45, w: 9, h: 1.0, fontSize: 26, bold: true, color: INK });
        sl.addText(dateIssued, { x: 0.45, y: 2.5, w: 7, h: 0.32, fontSize: 13, color: MUTED, italics: true });
        divider(sl, 0.45, 3.0, 12.5);
        const meta = [
          ['DATE ISSUED',      dateIssued],
          ['REPORT ID',        reportId],
          ['CLASSIFICATION',   'ANALYTICAL OUTPUT'],
          ['ANALYTICAL MODE',  analyticalMode.toUpperCase()],
          ['DISTRIBUTION',     'Authorized Users'],
          ['NEXT REVIEW',      nextReview],
        ];
        const cw = (W - 0.45 - 0.3) / 3;
        meta.forEach(([lbl, val], i) => {
          const col = i % 3, row = Math.floor(i / 3);
          const x = 0.45 + col * cw, y = 3.15 + row * 0.7;
          sl.addText(lbl, { x, y, w: cw - 0.1, h: 0.2, fontSize: 7, color: MUTED, bold: true, charSpacing: 1.2 });
          sl.addText(val, { x, y: y + 0.22, w: cw - 0.1, h: 0.28, fontSize: 11, color: ACCENT, italics: true, bold: true });
        });
        addFooter(sl, 1);
      }

      // ── SLIDE 2: EXECUTIVE SUMMARY ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Executive Summary');
        eyebrow(sl, 'ASSESSMENT', PAD, BY + 0.05, 5);
        sl.addText(reportTitle, { x: PAD, y: BY + 0.3, w: 9, h: 0.5, fontSize: 18, bold: true, color: INK });
        divider(sl, PAD, BY + 0.88, W - PAD * 2);
        const execSummary = recommendation?.rationale || executionOutput.slice(0, 600);
        sl.addText(execSummary, { x: PAD, y: BY + 1.0, w: W - PAD * 2, h: 2.5, fontSize: 11, color: INK2, wrap: true, valign: 'top' });
        const cards = [
          ['ANALYTICAL LEVEL',  'Assessed'],
          ['PRIMARY TECHNIQUE',  recommendation?.primaryTechnique || 'Structured Analysis'],
          ['ANALYTICAL MODE',    analyticalMode],
        ];
        const cw = (W - PAD * 2 - 0.2) / 3;
        cards.forEach(([lbl, val], i) => {
          const x = PAD + i * (cw + 0.1);
          sl.addShape(pptx.ShapeType.rect, { x, y: 5.5, w: cw, h: 0.85, fill: { color: WHITE }, line: { color: DIV, width: 0.5 } });
          sl.addText(lbl, { x: x + 0.12, y: 5.55, w: cw - 0.2, h: 0.22, fontSize: 7, color: MUTED, bold: true, charSpacing: 1 });
          sl.addText(val, { x: x + 0.12, y: 5.78, w: cw - 0.2, h: 0.45, fontSize: 13, color: ACCENT, bold: true });
        });
        addFooter(sl, 2);
      }

      // ── SLIDE 3: ANALYTICAL WORKFLOW ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Analytical Workflow');
        eyebrow(sl, 'KEY STEPS', PAD, BY + 0.05, 5);
        const steps = recommendation?.workflow || [];
        const rowH = 0.74, startY = BY + 0.38;
        steps.slice(0, 5).forEach((step, i) => {
          const y = startY + i * (rowH + 0.06);
          sl.addShape(pptx.ShapeType.rect, { x: PAD, y, w: W - PAD * 2, h: rowH, fill: { color: WHITE }, line: { color: DIV, width: 0.5 } });
          sl.addShape(pptx.ShapeType.rect, { x: PAD, y, w: 0.05, h: rowH, fill: { color: ACCENT }, line: { color: ACCENT } });
          sl.addText(String(i + 1).padStart(2, '0'), { x: PAD + 0.12, y: y + 0.22, w: 0.3, h: 0.3, fontSize: 9, bold: true, color: ACCENT });
          sl.addText(step, { x: PAD + 0.45, y: y + 0.12, w: W - PAD * 2 - 0.55, h: rowH - 0.2, fontSize: 10, color: INK, wrap: true, valign: 'middle' });
        });
        if (steps.length === 0) {
          sl.addText('No workflow steps defined for this analysis.', { x: PAD, y: BY + 0.6, w: W - PAD * 2, h: 0.4, fontSize: 12, color: MUTED, italics: true });
        }
        addFooter(sl, 3);
      }

      // ── SLIDE 4: ANALYSIS (Part 1) ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Analysis Output');
        eyebrow(sl, 'ANALYTICAL FINDINGS', PAD, BY + 0.05, 5);
        divider(sl, PAD, BY + 0.35, W - PAD * 2);
        const part1 = executionOutput.slice(0, 1400).replace(/#{1,3}\s*/g, '');
        sl.addText(part1, { x: PAD, y: BY + 0.45, w: W - PAD * 2, h: H - BY - FTR - 0.65, fontSize: 10, color: INK2, wrap: true, valign: 'top' });
        addFooter(sl, 4);
      }

      // ── SLIDE 5: ANALYSIS (Part 2) ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Analysis Output (cont.)');
        eyebrow(sl, 'ANALYTICAL FINDINGS — CONTINUED', PAD, BY + 0.05, 7);
        divider(sl, PAD, BY + 0.35, W - PAD * 2);
        const part2 = executionOutput.length > 1400
          ? executionOutput.slice(1400, 3000).replace(/#{1,3}\s*/g, '')
          : '(See previous slide for full analysis.)';
        sl.addText(part2, { x: PAD, y: BY + 0.45, w: W - PAD * 2, h: H - BY - FTR - 0.65, fontSize: 10, color: INK2, wrap: true, valign: 'top' });
        addFooter(sl, 5);
      }

      // ── SLIDE 6: MONITORING INDICATORS ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'Monitoring Indicators');
        eyebrow(sl, 'KEY SIGNALS TO TRACK', PAD, BY + 0.05, 6);
        const recCols = [2.5, 5.2, 2.0, 1.5];
        const recHdrs = ['INDICATOR', 'DESCRIPTION', 'SOURCE', 'FREQUENCY'];
        const hry = BY + 0.38;
        let hx = PAD;
        recHdrs.forEach((h, i) => {
          sl.addShape(pptx.ShapeType.rect, { x: hx, y: hry, w: recCols[i] - 0.04, h: 0.28, fill: { color: ACCENT }, line: { color: ACCENT } });
          sl.addText(h, { x: hx + 0.06, y: hry + 0.04, w: recCols[i] - 0.1, h: 0.2, fontSize: 7, bold: true, color: WHITE, charSpacing: 1 });
          hx += recCols[i];
        });
        if (indicators.length > 0) {
          indicators.slice(0, 7).forEach((ind, i) => {
            const ry = hry + 0.28 + i * 0.62;
            let rx = PAD;
            const rowColor = i % 2 === 0 ? WHITE : BG;
            [[ind.name || '—', INK], [ind.description || '—', INK2], [ind.source || '—', MUTED], [ind.frequency || '—', MUTED]].forEach(([val, clr], ci) => {
              sl.addShape(pptx.ShapeType.rect, { x: rx, y: ry, w: recCols[ci] - 0.04, h: 0.58, fill: { color: rowColor }, line: { color: DIV, width: 0.5 } });
              sl.addText(val, { x: rx + 0.08, y: ry + 0.06, w: recCols[ci] - 0.15, h: 0.46, fontSize: 9, color: clr, wrap: true, valign: 'middle' });
              rx += recCols[ci];
            });
          });
        } else {
          sl.addText('No monitoring indicators defined for this analysis.', { x: PAD, y: hry + 0.6, w: W - PAD * 2, h: 0.4, fontSize: 12, color: MUTED, italics: true });
        }
        addFooter(sl, 6);
      }

      // ── SLIDE 7: END ──
      {
        const sl = pptx.addSlide();
        sl.background = { color: BG };
        addHeader(sl, 'End of Briefing');
        sl.addShape(pptx.ShapeType.rect, { x: W / 2 - 2.5, y: 2.8, w: 5, h: 0.04, fill: { color: ACCENT }, line: { color: ACCENT } });
        sl.addText('Loyal Sidekick', { x: 0, y: 3.0, w: W, h: 0.7, fontSize: 28, bold: true, color: INK, align: 'center' });
        sl.addShape(pptx.ShapeType.rect, { x: W / 2 - 0.5, y: 3.78, w: 1.0, h: 0.05, fill: { color: GOLD }, line: { color: GOLD } });
        sl.addText('Structured Intelligence for Critical Questions', { x: 0, y: 3.95, w: W, h: 0.32, fontSize: 11, color: MUTED, align: 'center' });
        sl.addText('TK Strategies Limited', { x: 0, y: 4.3, w: W, h: 0.28, fontSize: 10, color: MUTED, align: 'center' });
        sl.addText('Next review: ' + nextReview + '  ·  Prepared for: Authorized Users', { x: 0, y: 5.0, w: W, h: 0.28, fontSize: 9, color: ACCENT, bold: true, align: 'center' });
        sl.addText('Generated by Loyal Sidekick. For named distribution only — do not forward without authorisation.', { x: W / 2 - 3.5, y: 5.4, w: 7, h: 0.28, fontSize: 8, color: MUTED, italics: true, align: 'center' });
        addFooter(sl, 7);
      }

      const filename = `loyal-sidekick-${new Date().toISOString().split('T')[0]}-${reportId}.pptx`;
      await pptx.writeFile({ fileName: filename });

      setMessages(prev => [...prev, {
        type: 'system',
        content: `PowerPoint exported: ${filename}`,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error exporting to PPTX:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        content: 'Failed to export PowerPoint. Please try again.',
        timestamp: new Date()
      }]);
    }
  };

  const sendToEmail = async () => {
    if (!executionOutput || !emailAddress.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress.trim())) {
      setMessages(prev => [...prev, {
        type: 'error',
        content: 'Please enter a valid email address.',
        timestamp: new Date()
      }]);
      return;
    }

    setEmailSending(true);
    try {
      const response = await fetch('/api/export-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailAddress.trim(),
          problem: diagnosticData.initialProblem,
          technique: recommendation?.primaryTechnique,
          content: executionOutput
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessages(prev => [...prev, {
          type: 'system',
          content: data.message || `Analysis sent to ${emailAddress.trim()}`,
          timestamp: new Date()
        }]);
        setShowEmailModal(false);
        setEmailAddress('');
      } else {
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'error',
        content: `Email error: ${error.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7f6f3',
      fontFamily: "'Inter', -apple-system, 'Helvetica Neue', Arial, sans-serif",
      color: '#1a1a1a',
      padding: '20px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          background: '#fff',
          border: '1px solid #e0ded9',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <a
              href="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#666',
                textDecoration: 'none',
                padding: '6px 12px',
                border: '1px solid #d4d2cc',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                marginRight: 'auto'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#2b5ea7';
                e.currentTarget.style.color = '#2b5ea7';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#d4d2cc';
                e.currentTarget.style.color = '#666';
              }}
            >
              <ArrowLeft size={14} />
              Back to Home
            </a>
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {usageInfo && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    title="View subscription & usage"
                    style={{
                      background: 'none', border: '1px solid #d4d2cc', borderRadius: '6px',
                      padding: '4px 10px', cursor: 'pointer', fontSize: '12px', color: '#555',
                      display: 'flex', alignItems: 'center', gap: '5px',
                    }}
                  >
                    <Zap size={12} color="#2b5ea7" />
                    {usageInfo.analyses_used_this_month}/{usageInfo.monthly_limit}
                    {usageInfo.subscription_tier === 'free'
                      ? <span style={{ color: '#2b5ea7', fontWeight: '700' }}>· Upgrade</span>
                      : <span style={{ color: '#888' }}>· {usageInfo.subscription_tier.charAt(0).toUpperCase() + usageInfo.subscription_tier.slice(1)}</span>
                    }
                  </button>
                )}
                <UserButton afterSignOutUrl="/" />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Brain size={32} style={{ color: '#2b5ea7' }} />
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: 0,
              color: '#2b5ea7',
              letterSpacing: '-0.01em'
            }}>
              Loyal Sidekick
            </h1>
          </div>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: '#666',
            letterSpacing: '0.02em'
          }}>
            STRUCTURED INTELLIGENCE × COGNITIVE SCIENCE × DISINFORMATION DEFENSE
          </p>
          
          {/* Persona selector */}
          {stage === 'intro' && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                fontSize: '12px',
                color: '#64748b',
                marginBottom: '8px',
                letterSpacing: '0.5px'
              }}>
                ANALYTICAL STANCE
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['balanced', 'mainstream', 'contrarian'].map(persona => (
                  <button
                    key={persona}
                    onClick={() => setSelectedPersona(persona)}
                    style={{
                      background: selectedPersona === persona ? '#2b5ea7' : '#fff',
                      color: selectedPersona === persona ? '#fff' : '#666',
                      border: selectedPersona === persona
                        ? '1px solid #2b5ea7'
                        : '1px solid #d4d2cc',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      letterSpacing: '0.02em',
                      transition: 'all 0.2s ease',
                      textTransform: 'uppercase'
                    }}
                  >
                    {persona === 'balanced' && <Zap size={14} style={{ display: 'inline', marginRight: '4px' }} />}
                    {persona === 'mainstream' && <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />}
                    {persona === 'contrarian' && <Target size={14} style={{ display: 'inline', marginRight: '4px' }} />}
                    {persona}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Progress indicator */}
          {stage === 'diagnostic' && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '11px',
                fontWeight: '500',
                color: '#666',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}>
                <span>Diagnostic Progress ({selectedPersona} stance)</span>
                <span>{diagnosticData.responses.length}/{TOTAL_QUESTIONS} questions</span>
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                background: '#e0ded9',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(diagnosticData.responses.length / TOTAL_QUESTIONS) * 100}%`,
                  height: '100%',
                  background: '#2b5ea7',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div style={{
          background: '#fff',
          border: '1px solid #e0ded9',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          minHeight: '500px',
          maxHeight: '600px',
          overflowY: 'auto',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
        }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.type === 'user' ? 'flex-end' : 'flex-start',
              animation: 'fadeIn 0.3s ease'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
                fontSize: '11px',
                fontWeight: '500',
                color: '#888',
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}>
                {msg.type === 'user' ? (
                  <Target size={14} style={{ color: '#2b5ea7' }} />
                ) : msg.type === 'system' ? (
                  <AlertCircle size={14} style={{ color: '#2b5ea7' }} />
                ) : msg.type === 'warning' ? (
                  <Shield size={14} style={{ color: '#d97706' }} />
                ) : msg.type === 'error' ? (
                  <AlertCircle size={14} style={{ color: '#c5221f' }} />
                ) : (
                  <Brain size={14} style={{ color: '#2b5ea7' }} />
                )}
                <span>
                  {msg.type === 'user' ? 'Analyst' : msg.type === 'system' ? 'System' : msg.type === 'warning' ? 'Security' : msg.type === 'error' ? 'Error' : 'Framework Coach'}
                </span>
              </div>
              <div style={{
                background: msg.type === 'user'
                  ? '#e8f0fe'
                  : msg.type === 'error'
                  ? '#fce8e6'
                  : msg.type === 'warning'
                  ? '#fef3cd'
                  : '#f8f7f3',
                border: `1px solid ${msg.type === 'user'
                  ? '#c5d9f7'
                  : msg.type === 'error'
                  ? '#f5c6c2'
                  : msg.type === 'warning'
                  ? '#f5d98c'
                  : '#e0ded9'}`,
                borderRadius: '8px',
                padding: '14px 18px',
                maxWidth: '85%',
                fontSize: '14px',
                lineHeight: '1.7',
                whiteSpace: 'pre-wrap',
                color: '#1a1a1a'
              }}>
                {msg.content}
              </div>
            </div>
          ))}

          {isProcessing && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#666',
              fontSize: '13px'
            }}>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite', color: '#2b5ea7' }} />
              <span>Processing with disinformation filters active...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Recommendation Display */}
        {(stage === 'recommendation' || stage === 'execution') && recommendation && (
          <div style={{
            background: '#fff',
            border: '2px solid #2e7d42',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <CheckCircle size={24} style={{ color: '#2e7d42' }} />
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                margin: 0,
                color: '#1a1a1a'
              }}>
                Technique Recommendation
              </h2>
              <div style={{
                marginLeft: 'auto',
                fontSize: '11px',
                fontWeight: '500',
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                {selectedPersona} stance
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#2b5ea7',
                marginBottom: '6px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}>
                Primary Technique
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#2b5ea7',
                marginBottom: '12px'
              }}>
                {recommendation.primaryTechnique}
              </div>

              {recommendation.secondaryTechnique && (
                <>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#666',
                    marginBottom: '6px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}>
                    Secondary Technique
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#4a4a4a',
                    marginBottom: '16px'
                  }}>
                    {recommendation.secondaryTechnique}
                  </div>
                </>
              )}

              <div style={{
                background: '#f8f7f3',
                border: '1px solid #e0ded9',
                borderRadius: '6px',
                padding: '14px',
                fontSize: '14px',
                lineHeight: '1.7',
                marginBottom: '20px',
                color: '#4a4a4a'
              }}>
                {recommendation.rationale}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#666',
                  marginBottom: '10px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}>
                  Identified Strengths
                </div>
                {recommendation.strengths.map((strength, idx) => (
                  <div key={idx} style={{
                    fontSize: '13px',
                    marginBottom: '8px',
                    paddingLeft: '12px',
                    borderLeft: '2px solid #2e7d42',
                    color: '#4a4a4a'
                  }}>
                    {strength}
                  </div>
                ))}
              </div>

              <div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#666',
                  marginBottom: '10px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}>
                  Analytical Watchouts
                </div>
                {recommendation.watchouts.map((watchout, idx) => (
                  <div key={idx} style={{
                    fontSize: '13px',
                    marginBottom: '8px',
                    paddingLeft: '12px',
                    borderLeft: '2px solid #d97706',
                    color: '#4a4a4a'
                  }}>
                    {watchout}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#666',
                marginBottom: '10px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}>
                Guided Workflow
              </div>
              {recommendation.workflow.map((step, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '10px',
                  fontSize: '13px'
                }}>
                  <div style={{
                    minWidth: '28px',
                    height: '28px',
                    borderRadius: '4px',
                    background: '#2b5ea7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#fff'
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1, paddingTop: '4px', color: '#4a4a4a' }}>
                    {step}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '16px',
              borderTop: '1px solid #e0ded9'
            }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Estimated time: {recommendation.estimatedTime}
              </div>
              {stage === 'recommendation' && (
                <button
                  onClick={executeFramework}
                  disabled={isProcessing}
                  style={{
                    background: '#2b5ea7',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    letterSpacing: '0.02em',
                    opacity: isProcessing ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <TrendingUp size={16} />
                  Execute Framework
                </button>
              )}
            </div>
          </div>
        )}

        {/* Execution Output */}
        {stage === 'execution' && executionOutput && (
          <>
            <div style={{
              background: '#fff',
              border: '1px solid #e0ded9',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  margin: 0,
                  color: '#1a1a1a'
                }}>
                  Analytical Framework Output
                </h2>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowEmailModal(true)}
                    style={{
                      background: '#2b5ea7',
                      color: '#fff',
                      border: '1px solid #2b5ea7',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Mail size={14} />
                    Send to Email
                  </button>
                  <button
                    onClick={exportToDOCX}
                    style={{
                      background: '#fff',
                      color: '#2b5ea7',
                      border: '1px solid #2b5ea7',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FileText size={14} />
                    Export to Word
                  </button>
                  <button
                    onClick={exportToPPTX}
                    style={{
                      background: '#fff',
                      color: '#666',
                      border: '1px solid #d4d2cc',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Presentation size={14} />
                    Export to PowerPoint
                  </button>
                </div>
              </div>
              <div style={{
                fontSize: '14px',
                lineHeight: '1.8',
                color: '#4a4a4a',
                whiteSpace: 'pre-wrap'
              }}>
                {executionOutput}
              </div>
            </div>

            {/* Indicator Dashboard */}
            {indicators.length > 0 && (
              <div style={{
                background: '#fff',
                border: '2px solid #2e7d42',
                borderRadius: '8px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  <BarChart3 size={24} style={{ color: '#2e7d42' }} />
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    margin: 0,
                    color: '#1a1a1a'
                  }}>
                    Indicator Monitoring Dashboard
                  </h2>
                </div>
                {indicators.map((indicator, idx) => (
                  <div key={idx} style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#2e7d42',
                      marginBottom: '8px'
                    }}>
                      {indicator.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a4a4a', marginBottom: '6px' }}>
                      <strong>Monitor:</strong> {indicator.description}
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a4a4a', marginBottom: '6px' }}>
                      <strong>Source:</strong> {indicator.source}
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a4a4a', marginBottom: '6px' }}>
                      <strong>Threshold:</strong> {indicator.threshold}
                    </div>
                    <div style={{ fontSize: '13px', color: '#4a4a4a' }}>
                      <strong>Frequency:</strong> {indicator.frequency}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setStage('intro');
                  setMessages([{
                    type: 'system',
                    content: 'Welcome back. What new analytical problem would you like to tackle?',
                    timestamp: new Date()
                  }]);
                  setDiagnosticData({ initialProblem: '', responses: [], currentQuestion: 0 });
                  setRecommendation(null);
                  setExecutionOutput(null);
                  setIndicators([]);
                }}
                style={{
                  background: '#2b5ea7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                  transition: 'all 0.2s ease'
                }}
              >
                Start New Analysis
              </button>
            </div>
          </>
        )}

        {/* Input Area */}
        {(stage === 'intro' || stage === 'diagnostic') && (
          <div style={{
            background: '#fff',
            border: '1px solid #e0ded9',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <textarea
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={stage === 'intro'
                  ? "Describe your analytical problem or question..."
                  : "Type your response..."}
                disabled={isProcessing}
                style={{
                  flex: 1,
                  background: '#f8f7f3',
                  border: '1px solid #d4d2cc',
                  borderRadius: '6px',
                  padding: '14px',
                  color: '#1a1a1a',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  minHeight: '80px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={handleSend}
                  disabled={!currentInput.trim() || isProcessing}
                  style={{
                    background: currentInput.trim() && !isProcessing ? '#2b5ea7' : '#e0ded9',
                    color: currentInput.trim() && !isProcessing ? 'white' : '#999',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '14px 20px',
                    cursor: currentInput.trim() && !isProcessing ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Send size={20} />
                </button>
                {stage === 'intro' && (
                  <button
                    onClick={skipToDiagnostic}
                    disabled={!currentInput.trim() || isProcessing}
                    style={{
                      background: currentInput.trim() && !isProcessing ? '#fff' : '#f0f0f0',
                      color: currentInput.trim() && !isProcessing ? '#666' : '#bbb',
                      border: '1px solid #d4d2cc',
                      borderRadius: '6px',
                      padding: '14px 20px',
                      cursor: currentInput.trim() && !isProcessing ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                    title="Skip diagnostic and get recommendation immediately"
                  >
                    <SkipForward size={16} />
                  </button>
                )}
              </div>
            </div>

            <div style={{
              marginTop: '12px',
              fontSize: '11px',
              color: '#888',
              letterSpacing: '0.02em'
            }}>
              <Shield size={12} style={{ display: 'inline', marginRight: '4px', color: '#2b5ea7' }} />
              Disinformation filters active • Press Enter to send • Shift + Enter for new line
            </div>
          </div>
        )}

        {/* Footer */}
        <footer style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '1px solid #e0ded9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          color: '#999'
        }}>
          <span>Loyal Sidekick — Structured intelligence for critical questions.</span>
          <span>TK Strategies Limited</span>
        </footer>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a1a1a'
              }}>
                Send Analysis to Email
              </h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailAddress('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#666'
                }}
              >
                <X size={20} />
              </button>
            </div>
            <p style={{
              fontSize: '14px',
              color: '#666',
              marginBottom: '16px'
            }}>
              Enter your email address to receive the complete analysis output.
            </p>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '14px',
                border: '1px solid #d4d2cc',
                borderRadius: '6px',
                marginBottom: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !emailSending) {
                  sendToEmail();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailAddress('');
                }}
                style={{
                  background: '#f8f7f3',
                  color: '#666',
                  border: '1px solid #d4d2cc',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendToEmail}
                disabled={emailSending || !emailAddress.trim()}
                style={{
                  background: emailSending || !emailAddress.trim() ? '#d4d2cc' : '#2b5ea7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: emailSending || !emailAddress.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {emailSending ? (
                  <>
                    <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy footer */}
      <div style={{
        marginTop: '32px',
        paddingTop: '16px',
        borderTop: '1px solid #e0ded9',
        fontSize: '11px',
        color: '#999',
        lineHeight: '1.6',
        textAlign: 'center'
      }}>
        <span style={{ fontWeight: '600', color: '#888' }}>Privacy &amp; AI Notice</span>
        {' · '}
        Loyal Sidekick is powered by{' '}
        <a href="https://www.anthropic.com" target="_blank" rel="noopener noreferrer"
          style={{ color: '#2b5ea7', textDecoration: 'none' }}>Claude (Anthropic)</a>.
        {' '}Your inputs transit Anthropic's API to generate responses and are{' '}
        <strong style={{ color: '#888' }}>not used to train AI models</strong>.
        {' '}Analysis content is automatically deleted after 30 days.
        {' '}Do not submit confidential, legally privileged, or sensitive personal information.
        {' '}AI outputs are not professional advice.
        {' · '}
        <button
          onClick={() => setShowPrivacyPolicy(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#2b5ea7',
            fontSize: '11px',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Full Privacy Statement
        </button>
      </div>

      {showPrivacyPolicy && (
        <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />
      )}

      {showUpgradeModal && (
        <UpgradeModal
          onClose={() => { setShowUpgradeModal(false); fetchSubscriptionDetail(); }}
          subscriptionDetail={subscriptionDetail}
          getToken={getToken}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f0f0f0;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: #d4d2cc;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #bbb;
        }

        textarea::placeholder {
          color: #999;
        }
      `}</style>
    </div>
  );
};

export default AnalyticalFrameworkTool;
